import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
    Background, Controls, Handle, Position, ReactFlowProvider, MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
    Box, Flex, HStack, VStack, Text, Heading, Badge, Button, ButtonGroup, IconButton, Input,
    SimpleGrid, Spinner, Table, Thead, Tbody, Tr, Th, Td, Progress,
    useBreakpointValue, useToast,
} from '@chakra-ui/react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend,
    ResponsiveContainer, Cell,
} from 'recharts';
import { FaSyncAlt, FaChartBar } from 'react-icons/fa';
import { painelUra } from '../../services/api';
import {
    ACCENT, DESFECHOS, visualDoNo, rotuloSaida, fmtSeg, fmtMs, fmtDiaCurto,
    diasAtras, hojeISO, pct,
} from './uraUtils';

const COR = {
    humano: '#38A169',
    bot: '#319795',
    ura_resolveu: '#3182CE',
    abandonou: '#E53E3E',
    sem_atendimento: '#D53F8C',
    transbordo: '#DD6B20',
    falha_tecnica: '#D69E2E',
    sem_saida: '#805AD5',
    em_aberto: '#90CDF4',
    sem_ura: '#CBD5E0',
};

/** "1.4" -> "1,4 dia". O comparativo passou a medir em dia fracionário. */
const fmtDias = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v) || v <= 0) return null;
    const arred = Math.round(v * 10) / 10;
    const txt = Number.isInteger(arred) ? String(arred) : arred.toFixed(1).replace('.', ',');
    return `${txt} ${arred === 1 ? 'dia' : 'dias'}`;
};

const LINHA = '#CBD5E0';

/* ==================================================================
 * Cartões
 * ================================================================== */

const Cartao = ({ rotulo, valor, detalhe, cor = 'gray', destaque, onClick }) => (
    <Box
        bg="white" border="1px solid" borderColor={destaque ? `${cor}.200` : 'gray.200'}
        borderRadius="lg" px={3} py={3} minW={0}
        onClick={onClick}
        cursor={onClick ? 'pointer' : 'default'}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
        } : undefined}
        _hover={onClick ? { borderColor: ACCENT, boxShadow: 'sm' } : {}}
        _focusVisible={{ outline: '2px solid', outlineColor: ACCENT }}
    >
        <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wide" noOfLines={1}>
            {rotulo}
        </Text>
        <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={destaque ? `${cor}.600` : 'gray.700'} lineHeight="1.2">
            {valor}
        </Text>
        {detalhe && <Text fontSize="10px" color="gray.500" noOfLines={1}>{detalhe}</Text>}
    </Box>
);

const Bloco = ({ titulo, descricao, children, acao }) => (
    <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" p={{ base: 4, md: 5 }}>
        <Flex justify="space-between" align="flex-start" gap={2} mb={descricao ? 1 : 3} wrap="wrap">
            <Heading as="h2" size="sm" color="gray.700">{titulo}</Heading>
            {acao}
        </Flex>
        {descricao && <Text fontSize="xs" color="gray.500" mb={3}>{descricao}</Text>}
        {children}
    </Box>
);

/**
 * Antes × depois da URA.
 *
 * É a única linha da tela que responde à pergunta que motivou a URA. A base de
 * "antes" sai das ligações que entravam direto no softphone, no mesmo número de
 * dias imediatamente anterior à virada.
 */
const Comparativo = ({ dados }) => {
    if (!dados || dados.antes?.taxa == null || dados.agora?.taxa == null) return null;

    // Arredondado: o servidor pode mandar taxa fracionada e "+3.0000000001
    // pontos" não é número que alguém leia.
    const delta = Math.round((Number(dados.agora.taxa) - Number(dados.antes.taxa)) * 10) / 10;

    const janela = fmtDias(dados.dias);
    const filtro = fmtDias(dados.periodo_dias);

    const Lado = ({ rotulo, bloco, cor }) => {
        const de = [bloco.inicio, bloco.inicio_hora].filter(Boolean).join(' ');
        const ate = [bloco.fim, bloco.fim_hora].filter(Boolean).join(' ');
        const dias = fmtDias(bloco.dias);
        return (
            <Box flex="1" minW="150px">
                <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wide">{rotulo}</Text>
                <Flex align="baseline" gap={2}>
                    <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={cor} lineHeight="1.2">
                        {bloco.taxa}%
                    </Text>
                    <Text fontSize="10px" color="gray.500">atendidas de {bloco.total}</Text>
                </Flex>
                {/* O período de cada lado é informativo: se o servidor não mandar,
                    a linha some — não vira "undefined a undefined" na tela. */}
                {(de || ate) && (
                    <Text fontSize="10px" color="gray.400">
                        {de} a {ate}{dias ? ` · ${dias}` : ''}
                    </Text>
                )}
            </Box>
        );
    };

    return (
        <Bloco
            titulo="Antes e depois da URA"
            descricao={`Quanta ligação chegou a ser atendida, aqui e no mesmo tanto de tempo antes da virada${dados.virada ? ` (${dados.virada})` : ''}.`}
            acao={
                <Badge colorScheme={delta >= 0 ? 'green' : 'red'} fontSize="11px">
                    {delta >= 0 ? '+' : ''}{delta} pontos
                </Badge>
            }
        >
            <Flex gap={4} wrap="wrap">
                <Lado rotulo="Antes (pelo softphone)" bloco={dados.antes} cor="gray.600" />
                <Lado rotulo="Com a URA" bloco={dados.agora} cor={delta >= 0 ? 'green.600' : 'red.600'} />
            </Flex>

            {/* A comparação NÃO cobre o período do filtro: o lado "com a URA"
                começa na virada do PABX. Sem dizer isto, 33 ligações de um dia e
                meio pareciam comparáveis a 168 de trinta dias. */}
            {janela && (
                <Text fontSize="10px" color="gray.500" mt={3}>
                    Comparando <b>{janela}</b> de cada lado{filtro ? `, dentro do filtro de ${filtro}` : ''} —
                    o lado "com a URA" começa na virada do PABX, não no início do período.
                </Text>
            )}
            <Text fontSize="10px" color="gray.400" mt={3}>
                O "antes" só enxerga a ligação que chegou a tocar no softphone: quem estava com a aba
                fechada não deixou registro. Ou seja, o número antigo é otimista — a comparação pesa
                contra a URA, não a favor.
            </Text>
        </Bloco>
    );
};

/**
 * O que acontece depois da ligação que ninguém atendeu.
 *
 * Sem isso o mesmo caminhoneiro ligando três vezes em dois minutos vira três
 * clientes perdidos no painel.
 */
const Retorno = ({ dados }) => {
    if (!dados || !dados.sem_atendimento) return null;

    const Fatia = ({ rotulo, valor, cor, detalhe }) => (
        <Box flex="1" minW="140px">
            <Flex align="baseline" gap={2}>
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={cor} lineHeight="1.2">{valor}</Text>
                <Text fontSize="10px" color="gray.500">{pct(valor, dados.sem_atendimento)}%</Text>
            </Flex>
            <Text fontSize="xs" fontWeight="semibold" color="gray.700">{rotulo}</Text>
            <Text fontSize="10px" color="gray.500">{detalhe}</Text>
        </Box>
    );

    const horas = Number(dados.janela_min) > 0 ? Math.round(dados.janela_min / 60) : null;
    const aguardando = Number(dados.aguardando) || 0;

    return (
        <Bloco
            titulo={`E depois? (${dados.sem_atendimento} ligações que ninguém atendeu)`}
            descricao={horas
                ? `O que aconteceu com o mesmo número nas ${horas} horas seguintes.`
                : 'O que aconteceu com o mesmo número nas horas seguintes.'}
        >
            <Flex gap={4} wrap="wrap">
                <Fatia rotulo="Resolvida depois" valor={dados.resolvido} cor="green.600" detalhe="voltou e foi atendida, ou a gente ligou" />
                <Fatia rotulo="Tentou de novo e não conseguiu" valor={dados.tentou_de_novo} cor="orange.500" detalhe="ligou outra vez e também não falou" />
                <Fatia rotulo="Sumiu" valor={dados.sumiu} cor="red.600" detalhe="não voltou e ninguém procurou — perda de verdade" />
                {/* Sem esta fatia a conta "some" no fim do período: as ligações
                    de agora ainda estão dentro da janela e não entram em nenhuma
                    das três acima. */}
                {aguardando > 0 && (
                    <Fatia
                        rotulo="Ainda em observação" valor={aguardando} cor="gray.600"
                        detalhe={horas ? `a janela de ${horas}h ainda não fechou` : 'a janela ainda não fechou'}
                    />
                )}
            </Flex>
        </Bloco>
    );
};

/* ==================================================================
 * O mapa de calor: o fluxo publicado com os números em cima
 * ================================================================== */

const CaixaCalor = ({ data }) => {
    const v = visualDoNo(data.tipo);
    const intensidade = data.maior > 0 ? data.ligacoes / data.maior : 0;

    return (
        <Box
            bg={data.ligacoes ? `${v.cor}.50` : 'white'}
            border="2px solid"
            borderColor={data.abandonos > 0 ? 'red.300' : `${v.cor}.200`}
            borderRadius="lg" px={3} py={2} w="210px" position="relative" boxShadow="sm"
            cursor={data.ligacoes ? 'pointer' : 'default'}
            _hover={data.ligacoes ? { boxShadow: 'md', borderColor: ACCENT } : {}}
        >
            <Handle type="target" position={Position.Top} style={{ background: LINHA, width: 8, height: 8 }} />

            <HStack spacing={2} align="center">
                <Box as={v.icone} color={`${v.cor}.500`} boxSize="12px" flexShrink={0} />
                <Text fontSize="xs" fontWeight="bold" color="gray.700" noOfLines={1}>{data.nome}</Text>
            </HStack>

            <Flex align="baseline" gap={2} mt={1}>
                <Text fontSize="lg" fontWeight="bold" color="gray.700" lineHeight="1">{data.ligacoes}</Text>
                <Text fontSize="10px" color="gray.500">ligações</Text>
                {data.tempo_medio_ms != null && (
                    <Text fontSize="10px" color="gray.400" ml="auto">{fmtMs(data.tempo_medio_ms)}</Text>
                )}
            </Flex>

            <Progress
                value={Math.round(intensidade * 100)} size="xs" mt={1} borderRadius="full"
                colorScheme={data.abandonos > 0 ? 'red' : v.cor}
            />

            {data.abandonos > 0 && (
                <Text fontSize="10px" color="red.500" mt={1}>
                    {data.abandonos} desligaram aqui
                </Text>
            )}

            <Handle type="source" position={Position.Bottom} style={{ background: LINHA, width: 8, height: 8 }} />
        </Box>
    );
};

const TIPOS_NO = { calor: CaixaCalor };

/** Arrumação de reserva, para o desenho que nunca foi posicionado na tela. */
const arrumar = (nos, inicio) => {
    const pos = {};
    const nivel = {};
    const vistos = {};
    const fila = inicio ? [[inicio, 0]] : [];

    while (fila.length) {
        const [id, n] = fila.shift();
        if (!nos[id] || vistos[id]) continue;
        vistos[id] = true;
        nivel[n] = nivel[n] || [];
        nivel[n].push(id);
        Object.values(nos[id].saidas || {}).forEach((d) => {
            if (d && nos[d] && !vistos[d]) fila.push([d, n + 1]);
        });
    }

    let direita = 0;
    Object.entries(nivel).forEach(([n, ids]) => {
        const largura = (ids.length - 1) * 280;
        ids.forEach((id, i) => {
            pos[id] = { x: i * 280 - largura / 2, y: Number(n) * 170 };
            direita = Math.max(direita, pos[id].x);
        });
    });

    // Órfão vai para uma faixa à direita do que já foi desenhado — x fixo caía
    // em cima da árvore assim que um nível ficava largo.
    Object.keys(nos).filter((id) => !vistos[id]).forEach((id, i) => {
        pos[id] = { x: direita + 320, y: i * 170 };
    });

    return pos;
};

/**
 * Afasta caixa que nasceu em cima da outra.
 *
 * As posições vêm do desenho, e desenho pode ter caixa colada (o fluxo padrão
 * da TAGA nascia com duas a 100 px de distância, com 210 px de largura). No
 * editor isso se resolve arrastando; aqui o mapa é só leitura, então quem tem
 * que se defender é ele. Só mexe em quem colide — o resto fica onde foi posto.
 */
const desempilhar = (pos, largura = 240, altura = 130) => {
    const ids = Object.keys(pos).sort((a, b) => (pos[a].y - pos[b].y) || (pos[a].x - pos[b].x));

    ids.forEach((id, i) => {
        let bateu = true;
        let voltas = 0;
        while (bateu && voltas++ <= ids.length) {
            bateu = false;
            for (let k = 0; k < i; k++) {
                const a = pos[id];
                const b = pos[ids[k]];
                if (Math.abs(a.x - b.x) < largura && Math.abs(a.y - b.y) < altura) {
                    a.x = b.x + largura;
                    bateu = true;
                }
            }
        }
    });

    return pos;
};

/**
 * O que o mapa NÃO mostra.
 *
 * Passo de versão anterior e caixa que não existe mais no desenho somem do
 * mapa; sem esta linha eles sumiam calados, e a soma das caixas não batia com
 * o total do período.
 */
const MapaFora = ({ dados }) => {
    if (!dados) return null;

    const fora = Array.isArray(dados.fora_do_desenho) ? dados.fora_do_desenho : [];
    const outras = Array.isArray(dados.outras_versoes) ? dados.outras_versoes : [];
    const outrasLigacoes = Number(dados.outras_versoes_ligacoes) || 0;
    if (!fora.length && !outras.length && !outrasLigacoes) return null;

    const nome = (f) => (typeof f === 'string' ? f : (f?.nome || f?.no_id || '?'));

    return (
        <Text fontSize="10px" color="gray.500" mt={2}>
            {dados.versao ? `O mapa é do fluxo v${dados.versao}` : 'O mapa é do fluxo que está no ar'}
            {Number(dados.ligacoes) ? `, com ${dados.ligacoes} ligação(ões)` : ''}.
            {(outrasLigacoes > 0 || outras.length > 0) && (
                ` ${outrasLigacoes || 'Algumas'} ligação(ões) passaram por versão anterior`
                + (outras.length ? ` (v${outras.join(', v')})` : '')
                + ' e não aparecem aqui.'
            )}
            {fora.length > 0 && (
                ` ${fora.length} caixa(s) do período não existem mais no desenho: `
                + fora.slice(0, 4).map(nome).join(', ')
                + (fora.length > 4 ? '…' : '') + '.'
            )}
        </Text>
    );
};

const MapaDoFluxo = ({ fluxo, porNo, transicoes, onEscolherNo, ehMobile }) => {
    const { nodes, edges } = useMemo(() => {
        const nos = fluxo?.grafo?.nos || {};
        const auto = arrumar(nos, fluxo?.grafo?.inicio);
        const porId = Object.fromEntries((porNo || []).map((n) => [n.no_id, n]));
        const maior = Math.max(1, ...(porNo || []).map((n) => n.ligacoes || 0));

        // Ou o desenho INTEIRO foi posicionado à mão, ou vale a arrumação
        // automática para todo mundo. Meio a meio mistura dois sistemas de
        // coordenadas na mesma tela e as caixas caem umas sobre as outras.
        const ids = Object.keys(nos);
        const desenhado = ids.length > 0 && ids.every((id) => {
            const p = nos[id].pos;
            return !!(p && (Number(p.x) || Number(p.y)));
        });

        const posicoes = {};
        ids.forEach((id) => {
            const p = desenhado ? nos[id].pos : (auto[id] || { x: 0, y: 0 });
            posicoes[id] = { x: Number(p.x) || 0, y: Number(p.y) || 0 };
        });
        desempilhar(posicoes);

        const nodes = Object.entries(nos).map(([id, no]) => {
            const dados = porId[id] || {};
            return {
                id,
                type: 'calor',
                position: posicoes[id],
                data: {
                    nome: no.nome || visualDoNo(no.tipo).rotulo,
                    tipo: no.tipo,
                    ligacoes: dados.ligacoes || 0,
                    abandonos: dados.abandonos || 0,
                    tempo_medio_ms: dados.tempo_medio_ms ?? null,
                    maior,
                },
            };
        });

        // Uma seta por transição que ACONTECEU. O desenho tem as saídas todas;
        // aqui só interessa por onde a ligação passou de verdade, e quantas.
        const maiorSeta = Math.max(1, ...(transicoes || []).map((t) => t.n || 0));
        const edges = (transicoes || [])
            .filter((t) => nos[t.de] && nos[t.para])
            .map((t) => ({
                id: `${t.de}::${t.evento}::${t.para}`,
                source: t.de,
                target: t.para,
                label: `${rotuloSaida(t.evento)} · ${t.n}`,
                type: 'smoothstep',
                markerEnd: { type: MarkerType.ArrowClosed, color: ACCENT },
                style: { stroke: ACCENT, strokeWidth: 1 + Math.round((t.n / maiorSeta) * 4), opacity: 0.75 },
                labelStyle: { fontSize: 10, fill: '#4A5568' },
                labelBgStyle: { fill: '#FFFFFF' },
                labelBgPadding: [4, 2],
                labelBgBorderRadius: 4,
            }));

        return { nodes, edges };
    }, [fluxo, porNo, transicoes]);

    if (!fluxo) {
        return <Text fontSize="xs" color="gray.500">Nenhum fluxo publicado — não há mapa para desenhar.</Text>;
    }

    return (
        <Box h={{ base: '430px', md: '520px' }} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
            <ReactFlowProvider>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={TIPOS_NO}
                    onNodeClick={(_, no) => no?.data?.ligacoes && onEscolherNo?.(no.id, no?.data?.nome)}
                    fitView
                    // O mapa é só leitura. No celular, o dedo que encostava aqui
                    // para rolar a página arrastava o mapa e a página não andava:
                    // quem navega são os botões de zoom.
                    panOnDrag={!ehMobile}
                    preventScrolling={!ehMobile}
                    // Sem piso o mapa nasce com a caixa em 50 px no celular —
                    // uma mancha. Melhor começar legível e cortado: arrastar
                    // resolve, adivinhar não.
                    fitViewOptions={{ padding: 0.08, minZoom: 0.45 }}
                    minZoom={0.2}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    proOptions={{ hideAttribution: true }}
                >
                    <Background gap={18} color="#E2E8F0" />
                    <Controls
                        showInteractive={false}
                        style={ehMobile ? { transform: 'scale(1.35)', transformOrigin: 'bottom left' } : undefined}
                    />
                </ReactFlow>
            </ReactFlowProvider>
        </Box>
    );
};

/* ==================================================================
 * A aba
 * ================================================================== */

const PainelTab = ({ onVerLigacoes, ativo = true }) => {
    const toast = useToast();
    const ehMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false });

    const [periodo, setPeriodo] = useState(() => ({ data_inicio: diasAtras(29), data_fim: hojeISO() }));
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const { data } = await painelUra(periodo);
            setDados(data.data || null);
        } catch (e) {
            toast({ title: 'Não consegui carregar o painel.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setCarregando(false);
        }
    }, [periodo, toast]);

    useEffect(() => { carregar(); }, [carregar]);

    const atalho = (dias) => setPeriodo({ data_inicio: diasAtras(dias - 1), data_fim: hojeISO() });

    const r = dados?.resumo || {};
    const total = r.total || 0;

    // Percentual sobre quem TEM caminho registrado. Ligação anterior ao
    // histórico (ou de quando a URA estava desligada) entra no total, mas
    // diluir o resto por ela faria a tela dizer "0% falou com vendedor" num dia
    // em que o vendedor atendeu.
    const base = r.total_ura || 0;

    // Levar o período junto é o que faz o número clicado bater com a lista que
    // abre: sem isso o clique leva para os 30 dias padrão.
    const verLigacoes = (extra) => onVerLigacoes?.({ ...periodo, ...extra });

    const porDia = useMemo(
        () => (dados?.por_dia || []).map((d) => ({ ...d, rotulo: fmtDiaCurto(d.dia) })),
        [dados]
    );

    // As horas mortas da madrugada só empurram o gráfico: mostra da primeira à
    // última hora em que o telefone tocou de verdade.
    const porHora = useMemo(() => {
        const linhas = dados?.por_hora || [];
        const comLigacao = linhas.filter((h) => h.total > 0).map((h) => h.hora);
        if (!comLigacao.length) return [];
        const de = Math.max(0, Math.min(...comLigacao) - 1);
        const ate = Math.min(23, Math.max(...comLigacao) + 1);
        return linhas.filter((h) => h.hora >= de && h.hora <= ate).map((h) => ({ ...h, rotulo: `${h.hora}h` }));
    }, [dados]);

    const porDigito = useMemo(
        () => (dados?.por_digito || []).map((d) => ({ ...d, rotulo: `tecla ${d.digito}` })),
        [dados]
    );

    if (carregando && !dados) {
        return <Flex justify="center" py={16}><Spinner color={ACCENT} /></Flex>;
    }

    return (
        <VStack align="stretch" spacing={4}>
            {/* período */}
            <Flex
                bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg"
                px={3} py={3} gap={3} align={{ base: 'stretch', md: 'center' }}
                direction={{ base: 'column', md: 'row' }} wrap="wrap"
            >
                <ButtonGroup size="xs" isAttached variant="outline">
                    <Button onClick={() => atalho(7)}>7 dias</Button>
                    <Button onClick={() => atalho(30)}>30 dias</Button>
                    <Button onClick={() => atalho(90)}>90 dias</Button>
                </ButtonGroup>
                <HStack spacing={2} flex="1">
                    <Input
                        size="xs" type="date" value={periodo.data_inicio}
                        onChange={(e) => setPeriodo((p) => ({ ...p, data_inicio: e.target.value }))}
                    />
                    <Text fontSize="xs" color="gray.500">até</Text>
                    <Input
                        size="xs" type="date" value={periodo.data_fim}
                        onChange={(e) => setPeriodo((p) => ({ ...p, data_fim: e.target.value }))}
                    />
                </HStack>
                <HStack>
                    {dados?.fluxo && (
                        <Badge colorScheme="green" fontSize="10px">fluxo v{dados.fluxo.versao}</Badge>
                    )}
                    <IconButton aria-label="Atualizar" icon={<FaSyncAlt />} size={ehMobile ? 'sm' : 'xs'} h={ehMobile ? '40px' : undefined} minW={ehMobile ? '40px' : undefined} variant="ghost" onClick={carregar} isLoading={carregando} />
                </HStack>
            </Flex>

            {/* cartões */}
            <SimpleGrid columns={{ base: 2, md: 4, xl: 6 }} spacing={3}>
                <Cartao
                    rotulo="Ligações" valor={total} onClick={() => verLigacoes({})}
                    detalhe={r.sem_ura ? `${r.sem_ura} sem histórico de caminho` : 'entraram pelo PABX'}
                />
                <Cartao
                    rotulo="Falou com a equipe" valor={r.humano || 0} cor="green" destaque
                    detalhe={`${pct(r.humano, base)}% de ${base}`}
                    onClick={() => verLigacoes({ desfecho: 'humano' })}
                />
                <Cartao
                    rotulo="Atendente virtual" valor={r.bot || 0} cor="teal" destaque
                    detalhe={r.bot > 0
                        ? `${r.bot_engatou || 0} engataram · ${r.bot_largou || 0} largaram${r.bot_sem_log ? ` · ${r.bot_sem_log} sem log` : ''}`
                        : `${pct(r.bot, base)}% de ${base}`}
                    onClick={() => verLigacoes({ desfecho: 'bot' })}
                />
                <Cartao
                    rotulo="Ouviu o recado" valor={r.ura_resolveu || 0} cor="blue" destaque
                    detalhe="ouviu a informação e desligou"
                    onClick={() => verLigacoes({ desfecho: 'ura_resolveu' })}
                />
                <Cartao
                    rotulo="Desligou no meio" valor={r.abandonou || 0} cor="red" destaque
                    detalhe={`${pct(r.abandonou, base)}% de ${base}`}
                    onClick={() => verLigacoes({ desfecho: 'abandonou' })}
                />
                <Cartao
                    rotulo="Ninguém pôde atender" valor={r.sem_atendimento || 0} cor="pink" destaque
                    detalhe="ouviu a despedida e desligou"
                    onClick={() => verLigacoes({ desfecho: 'sem_atendimento' })}
                />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 2, md: 4, xl: 6 }} spacing={3}>
                <Cartao rotulo="Esperou (média)" valor={fmtSeg(r.espera_media_seg)} detalhe="até alguém atender" />
                <Cartao rotulo="Tempo na URA" valor={fmtSeg(r.tempo_ura_medio_seg)} detalhe="média por ligação" />
                <Cartao rotulo="Conversa (média)" valor={fmtSeg(r.conversa_media_seg)} detalhe="só quem foi atendido" />
                <Cartao rotulo="Não digitaram" valor={r.sem_resposta || 0} detalhe="menu terminou sem tecla" />
                <Cartao rotulo="Opção errada" valor={r.invalido || 0} detalhe="tecla que o menu não tem" />
                <Cartao rotulo="Fora do horário" valor={r.fora_horario || 0} detalhe="caíram fora da grade" />
                {r.em_aberto > 0 && (
                    <Cartao
                        rotulo="Ainda na linha" valor={r.em_aberto} cor="blue" destaque
                        detalhe="ligação que ainda não terminou"
                        onClick={() => verLigacoes({ desfecho: 'em_aberto' })}
                    />
                )}
            </SimpleGrid>

            {(r.transbordo > 0 || r.sem_saida > 0 || r.falha_tecnica > 0) && (
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                    {r.transbordo > 0 && (
                        <Cartao
                            rotulo="Devolvidas p/ operadora" valor={r.transbordo} cor="orange" destaque
                            onClick={() => verLigacoes({ desfecho: 'transbordo' })}
                        />
                    )}
                    {r.falha_tecnica > 0 && (
                        <Cartao
                            rotulo="Falha técnica" valor={r.falha_tecnica} cor="yellow" destaque
                            detalhe="a ligação caiu"
                            onClick={() => verLigacoes({ desfecho: 'falha_tecnica' })}
                        />
                    )}
                    {r.sem_saida > 0 && (
                        <Cartao
                            rotulo="O desenho deixou sem saída" valor={r.sem_saida} cor="purple" destaque
                            detalhe="ouviram a despedida de emergência"
                            onClick={() => verLigacoes({ desfecho: 'sem_saida' })}
                        />
                    )}
                </SimpleGrid>
            )}

            <Comparativo dados={dados?.comparativo} />
            <Retorno dados={dados?.retorno} />

            {/* volume por dia */}
            <Bloco titulo="Dia a dia" descricao="Cada barra é um dia, repartida pelo que aconteceu com a ligação.">
                {porDia.length ? (
                    <Box h={{ base: '220px', md: '280px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={porDia} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" />
                                <XAxis dataKey="rotulo" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                <ReTooltip contentStyle={{ fontSize: 12 }} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="humano" name={DESFECHOS.humano.curto} stackId="a" fill={COR.humano} />
                                <Bar dataKey="bot" name={DESFECHOS.bot.curto} stackId="a" fill={COR.bot} />
                                <Bar dataKey="ura_resolveu" name={DESFECHOS.ura_resolveu.curto} stackId="a" fill={COR.ura_resolveu} />
                                <Bar dataKey="abandonou" name={DESFECHOS.abandonou.curto} stackId="a" fill={COR.abandonou} />
                                <Bar dataKey="sem_atendimento" name="Sem atendimento / falhou" stackId="a" fill={COR.sem_atendimento} />
                                {/* Sem estas duas o dia com 12 ligações sem caminho
                                    aparecia vazio: as fatias agora somam o total. */}
                                <Bar dataKey="em_aberto" name="Ainda na linha" stackId="a" fill={COR.em_aberto} />
                                <Bar dataKey="sem_ura" name="Sem histórico" stackId="a" fill={COR.sem_ura} radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                ) : (
                    <Text fontSize="xs" color="gray.500">Sem ligação no período.</Text>
                )}
            </Bloco>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                <Bloco titulo="Hora do dia" descricao="Quando o telefone toca — e quando o cliente desiste.">
                    {porHora.length ? (
                        <Box h="230px">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={porHora} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" />
                                    <XAxis dataKey="rotulo" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                    <ReTooltip contentStyle={{ fontSize: 12 }} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Bar dataKey="total" name="Ligações" fill={ACCENT} radius={[3, 3, 0, 0]} />
                                    <Bar dataKey="abandonou" name="Desligaram" fill={COR.abandonou} radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    ) : (
                        <Text fontSize="xs" color="gray.500">Sem ligação no período.</Text>
                    )}
                </Bloco>

                <Bloco titulo="O que o cliente digitou" descricao="Cada tecla apertada no menu, contada uma vez por ligação.">
                    {porDigito.length ? (
                        <Box h="230px">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={porDigito} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" />
                                    <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                                    <YAxis type="category" dataKey="rotulo" tick={{ fontSize: 10 }} width={62} />
                                    <ReTooltip contentStyle={{ fontSize: 12 }} />
                                    <Bar dataKey="n" name="Ligações" radius={[0, 3, 3, 0]}>
                                        {porDigito.map((d) => <Cell key={d.digito} fill={ACCENT} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    ) : (
                        <Text fontSize="xs" color="gray.500">Ninguém digitou nada no período.</Text>
                    )}
                </Bloco>
            </SimpleGrid>

            {/* mapa de calor */}
            <Bloco
                titulo="Por onde as ligações passaram"
                descricao="O fluxo que está no ar, com o número de ligações em cada caixa e a grossura da seta pelo caminho mais usado. Clique numa caixa para ver essas ligações."
                acao={ehMobile ? <Badge colorScheme="gray" fontSize="10px">use + e − para navegar</Badge> : null}
            >
                {ativo && <MapaDoFluxo
                    fluxo={dados?.fluxo}
                    porNo={dados?.por_no}
                    transicoes={dados?.transicoes}
                    ehMobile={ehMobile}
                    onEscolherNo={(no, nome) => verLigacoes({ no, no_nome: nome, versao: dados?.fluxo?.versao })}
                />}
                <MapaFora dados={dados?.mapa_fora} />
            </Bloco>

            {/* tabela por caixa */}
            <Bloco titulo="Caixa por caixa" descricao="Onde o tempo é gasto e onde a ligação morre.">
                {dados?.por_no?.length ? (
                    <Box overflowX="auto">
                        <Table size="sm">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th fontSize="10px">Caixa</Th>
                                    <Th fontSize="10px" isNumeric>Ligações</Th>
                                    <Th fontSize="10px" isNumeric>Passagens</Th>
                                    <Th fontSize="10px" isNumeric>Desligaram aqui</Th>
                                    <Th fontSize="10px" isNumeric>Tempo médio</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {dados.por_no.map((n) => {
                                    const v = visualDoNo(n.tipo);
                                    return (
                                        <Tr
                                            key={n.no_id} _hover={{ bg: 'orange.50' }} cursor="pointer"
                                            onClick={() => verLigacoes({ no: n.no_id, no_nome: n.nome, versao: dados?.fluxo?.versao })}
                                        >
                                            <Td fontSize="xs">
                                                <HStack spacing={2}>
                                                    <Box as={v.icone} color={`${v.cor}.500`} boxSize="11px" />
                                                    <Text noOfLines={1}>{n.nome || n.no_id}</Text>
                                                    <Badge fontSize="9px" colorScheme={v.cor} variant="subtle">{v.rotulo}</Badge>
                                                </HStack>
                                            </Td>
                                            <Td fontSize="xs" isNumeric fontWeight="semibold">{n.ligacoes}</Td>
                                            <Td fontSize="xs" isNumeric color="gray.500">{n.passagens}</Td>
                                            <Td fontSize="xs" isNumeric color={n.abandonos ? 'red.500' : 'gray.400'}>
                                                {n.abandonos || '—'}
                                            </Td>
                                            <Td fontSize="xs" isNumeric color="gray.500">{fmtMs(n.tempo_medio_ms)}</Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                ) : (
                    <Flex direction="column" align="center" py={8}>
                        <Box as={FaChartBar} color="gray.300" boxSize="24px" mb={2} />
                        <Text fontSize="sm" color="gray.500">Nenhuma ligação passou pelo fluxo no período.</Text>
                    </Flex>
                )}
            </Bloco>

            <Text fontSize="10px" color="gray.400" textAlign="center" px={2}>
                Só entram aqui as ligações que chegaram pelo PABX. Ligação feita pelo discador tem tela
                própria, em /discador.
            </Text>
        </VStack>
    );
};

export default PainelTab;
